/**
 * TextIntakeModal
 *
 * Shared modal for the text-intake feature (Story 7.73 / #1149), restyled to
 * the "Extract from email or text" design: title + helper copy, upload/reset
 * icon buttons above a rich-text paste area (#1106 TextAreaEditor), and a
 * Cancel / Fill Form footer. Used by BOTH entry points — Create Request from
 * Text and the Paste text action on Request Detail — the only difference being
 * which mutation the parent runs on submit.
 *
 * The editor holds rich HTML, but the pipeline is plain-text: the char
 * counter, email-thread detection, and the submitted payload all use the
 * plain-text projection (`convertToPlainText`), matching the server's
 * text-intake contract.
 */

import {
  pick,
  types,
  isErrorWithCode,
  errorCodes,
} from '@react-native-documents/picker';
import { Check, RotateCcw, Upload, WandSparkles } from 'lucide-react-native';
import React, { useEffect, useMemo, useRef, useState } from 'react';
import {
  View,
  Text,
  TouchableOpacity,
  StyleSheet,
  Modal,
  ActivityIndicator,
  Animated,
  Keyboard,
  Platform,
  Pressable,
} from 'react-native';
import * as RNFS from 'react-native-fs';

import { REQUEST_FORM_KEYBOARD_BOTTOM_OFFSET } from '../../constants/requestFormLayout';
import {
  FormKeyboardScrollProvider,
  useRichTextKeyboardDismissBar,
} from '../../contexts/FormKeyboardScrollContext';
import { normalizeFileUri } from '../../services/utils/fileUtils';
import { colors } from '../../theme/colors';
import { convertToPlainText } from '../../utils/richContent/convertToPlainText';
import { showToast } from '../../utils/toast';
import { ModalKeyboardProvider } from '../shared/ModalKeyboardProvider';
import { RichTextKeyboardDismissBar } from '../shared/RichTextKeyboardDismissBar';
import { TextAreaEditor } from '../textArea/TextAreaEditor';

import type { InlineImageResolution } from '../textArea/inlineImage.types';
import type { ScrollView } from 'react-native';

/**
 * Both native modules are aliased to `false` for the web bundle
 * (webpack.config.js), and neither is reachable from the web branch of
 * `handleUploadPress`, so these static imports are safe on every platform —
 * the same shape `FileUpload.tsx` and `attachmentService.ts` already ship.
 */

/** Mirrors the server cap in server/utils/textIntakeSource.ts. */
export const TEXT_INTAKE_MAX_CHARS = 100000;
const NEAR_CAP_RATIO = 0.9;

/**
 * Refuse an upload far past the character cap before reading it — loading
 * megabytes of text into the editor WebView wedges it, and the paste would be
 * rejected by the cap anyway. Generous multiple of the cap so multi-byte
 * scripts are not penalised for their encoding.
 */
const TEXT_INTAKE_MAX_UPLOAD_BYTES = 2 * 1024 * 1024;

export interface TextIntakeSubmitOptions {
  /**
   * Explicit email-thread signal: true/false when the user touched the toggle,
   * undefined when untouched (server auto-detects on the normalized text).
   */
  emailThread?: boolean;
}

export interface TextIntakeModalProps {
  visible: boolean;
  onClose: () => void;
  onSubmit: (
    text: string,
    options: TextIntakeSubmitOptions,
  ) => void | Promise<void>;
  isSubmitting?: boolean;
  title?: string;
  submitLabel?: string;
}

/**
 * Lightweight client mirror of the server-side email-thread detection
 * (server/services/intake/intakeAdapter.ts) — used only to pre-check the
 * toggle; the server makes the final call.
 */
export function looksLikeEmailThread(text: string): boolean {
  if (!text) return false;
  if (/^(from|sent|to|cc|subject|date)\s*:/im.test(text)) return true;
  if (/^(>+\s|on .{4,80} wrote\s*:)/im.test(text)) return true;
  return (
    /^(regards|best regards|kind regards|thanks|thank you|sincerely)[,.!]?\s*$/im.test(
      text,
    ) && /\S+@\S+\.\S+/.test(text)
  );
}

function escapeHtml(value: string): string {
  return value
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;');
}

/**
 * Uploaded .txt content -> simple paragraphs the rich editor can hold.
 *
 * Line endings are normalized first. A .txt exported from Outlook or Notepad —
 * the likeliest source for this feature — is CRLF with a UTF-8 BOM, and
 * splitting on '\n' alone would leave a stray '\r' at the end of every
 * paragraph that then survives into the submitted payload.
 */
function plainTextToHtml(text: string): string {
  return text
    .replace(/^\uFEFF/, '')
    .replace(/\r\n?/g, '\n')
    .split('\n')
    .map(line => (line.trim() ? `<p>${escapeHtml(line)}</p>` : '<p></p>'))
    .join('');
}

/** The paste area is text-only — inline images are refused. */
const refuseInlineImage = async (): Promise<InlineImageResolution> => ({
  ok: false,
  error: 'Images are not supported here — paste text only.',
});

/**
 * Tap-outside-the-paste-area dismisses the KEYPAD — never the modal (#1149
 * Issue 8). Closing on a backdrop tap would throw away a long paste, so this
 * deliberately does not call onClose, unlike most backdrops in the app.
 *
 * Its own component because the dismiss hook has to resolve against the
 * isolated chrome provider, which only exists below it in the tree. 10tap runs
 * in a WKWebView whose contenteditable `Keyboard.dismiss()` cannot resign, so
 * the registered rich-text dismiss has to run too.
 */
const KeyboardDismissBackdrop: React.FC<{ disabled?: boolean }> = ({
  disabled,
}) => {
  const { dismiss } = useRichTextKeyboardDismissBar();
  return (
    <Pressable
      style={StyleSheet.absoluteFill}
      onPress={() => {
        if (disabled) {
          return;
        }
        dismiss();
        Keyboard.dismiss();
      }}
      accessibilityRole="button"
      accessibilityLabel="Dismiss keyboard"
      testID="text-intake-backdrop"
    />
  );
};

/**
 * Skeleton hint card for the empty paste area (per the design mockup): a
 * small ghosted "email" card with pulsing blue/grey lines that signals a
 * large block of text belongs here.
 */
const SKELETON_LINES = [
  { width: '78%', tone: 'blue' },
  { width: '92%', tone: 'grey' },
  { width: '62%', tone: 'blue' },
  { width: '84%', tone: 'blue' },
  { width: '42%', tone: 'blue' },
] as const;

/** Gap between the placeholder's baseline and the ghost card. */
const SKELETON_GAP = 10;
/** Native fallback: single-row toolbar (~48) + placeholder line + gap. */
const SKELETON_NATIVE_TOP = 88;
const SKELETON_NATIVE_LEFT = 16;

const PasteSkeletonHint: React.FC<{ top: number; left: number }> = ({
  top,
  left,
}) => {
  const pulse = useRef(new Animated.Value(0.55)).current;

  useEffect(() => {
    const loop = Animated.loop(
      Animated.sequence([
        Animated.timing(pulse, {
          toValue: 1,
          duration: 900,
          useNativeDriver: true,
        }),
        Animated.timing(pulse, {
          toValue: 0.55,
          duration: 900,
          useNativeDriver: true,
        }),
      ]),
    );
    loop.start();
    return () => loop.stop();
  }, [pulse]);

  return (
    <View
      style={[styles.skeletonOverlay, { top, left }]}
      pointerEvents="none"
      testID="text-intake-skeleton"
    >
      <Animated.View style={[styles.skeletonCard, { opacity: pulse }]}>
        {SKELETON_LINES.map((line, index) => (
          <View
            key={index}
            style={[
              styles.skeletonLine,
              { width: line.width },
              line.tone === 'blue'
                ? styles.skeletonLineBlue
                : styles.skeletonLineGrey,
            ]}
          />
        ))}
      </Animated.View>
    </View>
  );
};

/**
 * Web: anchor the skeleton to the editor's real placeholder element (the
 * toolbar can wrap to multiple rows on narrow widths, so a fixed offset is
 * wrong). Re-measures on wrapper layout changes; retries briefly while the
 * async editor boots. Native (WebView editor — nothing to query) keeps the
 * single-row-toolbar constant.
 */
function useSkeletonPosition(
  wrapperRef: React.RefObject<View | null>,
  active: boolean,
): { position: { top: number; left: number } | null; onLayout: () => void } {
  const [position, setPosition] = useState<{
    top: number;
    left: number;
  } | null>(
    Platform.OS === 'web'
      ? null
      : { top: SKELETON_NATIVE_TOP, left: SKELETON_NATIVE_LEFT },
  );

  const measure = React.useCallback((): boolean => {
    if (Platform.OS !== 'web') {
      return true;
    }
    const node = wrapperRef.current as unknown as HTMLElement | null;
    if (!node || typeof node.querySelector !== 'function') {
      return false;
    }
    const placeholder = node.querySelector(
      '[data-testid="pd-richtext-placeholder"]',
    ) as HTMLElement | null;
    if (!placeholder) {
      return false;
    }
    const wrapperRect = node.getBoundingClientRect();
    const placeholderRect = placeholder.getBoundingClientRect();
    setPosition({
      top: placeholderRect.bottom - wrapperRect.top + SKELETON_GAP,
      left: placeholderRect.left - wrapperRect.left,
    });
    return true;
  }, [wrapperRef]);

  useEffect(() => {
    if (!active || Platform.OS !== 'web') {
      return;
    }
    if (measure()) {
      return;
    }
    // The Tiptap editor (and its placeholder) mounts asynchronously.
    let tries = 0;
    const timer = setInterval(() => {
      tries += 1;
      if (measure() || tries >= 20) {
        clearInterval(timer);
      }
    }, 100);
    return () => clearInterval(timer);
  }, [active, measure]);

  return { position, onLayout: () => measure() };
}

export const TextIntakeModal: React.FC<TextIntakeModalProps> = ({
  visible,
  onClose,
  onSubmit,
  isSubmitting = false,
  title = 'Extract from email or text',
  submitLabel = 'Fill Form',
}) => {
  const [html, setHtml] = useState('');
  // Remount key + content for programmatic editor sets (reset / file upload).
  const [editorSeed, setEditorSeed] = useState(0);
  const [seedHtml, setSeedHtml] = useState('');
  const [emailThread, setEmailThread] = useState(false);
  const [emailThreadTouched, setEmailThreadTouched] = useState(false);
  /**
   * iOS: hide the modal while the document picker is on screen, because the
   * two cannot be presented at once. See `handleUploadPress`.
   */
  const [pickerSuspended, setPickerSuspended] = useState(false);
  const pendingPickRef = useRef(false);
  // Web-only hidden <input type="file"> host, created lazily on first use.
  const webFileInputRef = useRef<HTMLInputElement | null>(null);
  const editorWrapperRef = useRef<View | null>(null);
  /**
   * Intentionally never attached: this modal has no ScrollView. Supplying a
   * live-but-null ref replaces the request form's scroll context that the modal
   * would otherwise inherit, so focusing the paste area cannot scroll the form
   * hidden behind the modal. Every path in useScrollFieldAboveKeyboard bails
   * when the ref is null.
   */
  const bodyScrollRef = useRef<ScrollView | null>(null);

  const plainText = useMemo(() => convertToPlainText(html), [html]);

  const handleHtmlChange = (next: string) => {
    setHtml(next);
    if (!emailThreadTouched) {
      setEmailThread(looksLikeEmailThread(convertToPlainText(next)));
    }
  };

  /** Replace the editor document programmatically (reset / upload). */
  const setEditorContent = (nextHtml: string) => {
    setSeedHtml(nextHtml);
    setEditorSeed(seed => seed + 1);
    setHtml(nextHtml);
    if (!emailThreadTouched) {
      setEmailThread(looksLikeEmailThread(convertToPlainText(nextHtml)));
    }
  };

  /**
   * Safety net for the iOS pick-suspend dance: `onDismiss` is the deterministic
   * signal, but if it ever failed to fire the modal would stay hidden with no
   * way back — a worse failure than the bug this works around. If the dismiss
   * callback has not claimed the pending pick shortly after we hide, run it
   * here instead.
   */
  useEffect(() => {
    if (!pickerSuspended || !pendingPickRef.current) {
      return undefined;
    }
    const timer = setTimeout(() => {
      if (!pendingPickRef.current) {
        return;
      }
      pendingPickRef.current = false;
      void runNativePick().finally(() => setPickerSuspended(false));
    }, 700);
    return () => clearTimeout(timer);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pickerSuspended]);

  const toggleEmailThread = () => {
    setEmailThreadTouched(true);
    setEmailThread(previous => !previous);
  };

  const resetState = () => {
    setEditorContent('');
    setEmailThread(false);
    setEmailThreadTouched(false);
  };

  const handleClose = () => {
    if (isSubmitting) {
      return;
    }
    resetState();
    onClose();
  };

  const handleSubmit = async () => {
    if (!canSubmit) {
      return;
    }
    await onSubmit(plainText, {
      emailThread: emailThreadTouched ? emailThread : undefined,
    });
    resetState();
  };

  /** Pick a .txt and load it into the paste area. Native only. */
  const runNativePick = async () => {
    try {
      const [picked] = await pick({ type: [types.plainText] });
      if (!picked) {
        return;
      }
      if ((picked.size ?? 0) > TEXT_INTAKE_MAX_UPLOAD_BYTES) {
        showToast.error(
          'That file is too large to paste. Use a smaller .txt file.',
        );
        return;
      }
      // RNFS — not fetch — because React Native cannot read a file:// (iOS) or
      // content:// (Android) URI over the network stack. normalizeFileUri
      // decodes spaced file:// names and leaves content:// document URIs encoded.
      const text = await RNFS.readFile(normalizeFileUri(picked.uri), 'utf8');
      setEditorContent(plainTextToHtml(text));
    } catch (e) {
      if (isErrorWithCode(e) && e.code === errorCodes.OPERATION_CANCELED) {
        return; // Backed out of the picker sheet — silent by design.
      }
      // This failure used to be silent, which is how the whole upload path
      // shipped broken. Say something.
      console.warn('Failed to read text file', e);
      showToast.error('Could not read that file. Try a plain .txt file.');
    }
  };

  /** Load a .txt file's content into the paste area. */
  const handleUploadPress = async () => {
    if (isSubmitting) {
      return;
    }
    if (Platform.OS === 'web') {
      if (!webFileInputRef.current && typeof document !== 'undefined') {
        const input = document.createElement('input');
        input.type = 'file';
        input.accept = '.txt,text/plain';
        input.style.display = 'none';
        input.onchange = async () => {
          const file = input.files?.[0];
          input.value = '';
          if (!file) return;
          try {
            setEditorContent(plainTextToHtml(await file.text()));
          } catch (e) {
            console.warn('Failed to read text file', e);
          }
        };
        document.body.appendChild(input);
        webFileInputRef.current = input;
      }
      webFileInputRef.current?.click();
      return;
    }
    if (Platform.OS === 'ios') {
      /**
       * iOS presents UIDocumentPickerViewController from the root view
       * controller — which is already presenting THIS Modal. The system
       * resolves that conflict by tearing our modal down, so the dialog just
       * vanishes and the picker never appears.
       *
       * So: hide the modal first and run the pick from `onDismiss`, once the
       * presentation slot is actually free. State lives above the <Modal>, so
       * hiding it does not lose the pasted text.
       *
       * The rest of the app avoids this by picking BEFORE opening a modal
       * (ViewRequests -> ImportPreCreationModal); text intake cannot, because
       * the upload button belongs inside the paste dialog.
       */
      pendingPickRef.current = true;
      setPickerSuspended(true);
      return;
    }
    await runNativePick();
  };

  /**
   * iOS only: the modal has finished dismissing, so the picker can present.
   * Restores the modal whatever the outcome — including cancel.
   */
  const handleModalDismissed = () => {
    if (!pendingPickRef.current) {
      return;
    }
    pendingPickRef.current = false;
    void runNativePick().finally(() => setPickerSuspended(false));
  };

  const charCount = plainText.length;
  const overCap = charCount > TEXT_INTAKE_MAX_CHARS;
  const nearCap = charCount >= TEXT_INTAKE_MAX_CHARS * NEAR_CAP_RATIO;
  const isBlank = plainText.trim().length === 0;
  const canSubmit = !isBlank && !overCap && !isSubmitting;

  const { position: skeletonPosition, onLayout: onEditorWrapperLayout } =
    useSkeletonPosition(editorWrapperRef, visible && isBlank);

  const counterColor = overCap
    ? colors.error
    : nearCap
    ? '#B7791F'
    : colors.textSecondary;

  return (
    <Modal
      visible={visible && !pickerSuspended}
      animationType="fade"
      transparent
      onRequestClose={handleClose}
      onDismiss={handleModalDismissed}
    >
      {/* #1149 Issue 8 — mobile keyboard chrome. Order is load-bearing:
          ModalKeyboardProvider first, because the Done bar positions itself at
          `bottom: keyboardHeight` and an RN Modal is its own window on iOS, so
          without a provider inside it that height reads 0 and the bar lands
          under the keypad. Then isolateChrome, so this modal's editor arms
          ITS OWN bar rather than the one mounted on the screen behind it. */}
      <ModalKeyboardProvider>
        <FormKeyboardScrollProvider
          scrollRef={bodyScrollRef}
          bottomOffset={REQUEST_FORM_KEYBOARD_BOTTOM_OFFSET}
          isolateChrome
        >
          <View style={styles.overlay}>
            <KeyboardDismissBackdrop disabled={isSubmitting} />
            <View style={styles.modalContainer}>
              {/* #1149 Issue 5: fixed chrome via a real flex column — header and
              footer flexShrink:0; only the editorWrapper absorbs leftover
              height and scrolls inside the rich-text scrollport. */}
              <View style={styles.header}>
                <Text style={styles.title}>{title}</Text>
                <Text style={styles.description}>
                  Paste an email or block of text below. We&apos;ll pull out
                  matching details and fill them into the form.
                </Text>

                <View style={styles.toolbarRow}>
                  <TouchableOpacity
                    style={styles.iconButton}
                    onPress={handleUploadPress}
                    disabled={isSubmitting}
                    accessibilityLabel="Upload a text file"
                    accessibilityRole="button"
                    testID="text-intake-upload"
                  >
                    <Upload size={18} color={colors.textSecondary} />
                  </TouchableOpacity>
                  <TouchableOpacity
                    style={styles.iconButton}
                    onPress={resetState}
                    disabled={isSubmitting}
                    accessibilityLabel="Clear text"
                    accessibilityRole="button"
                    testID="text-intake-reset"
                  >
                    <RotateCcw size={18} color={colors.textSecondary} />
                  </TouchableOpacity>
                </View>
              </View>

              <View
                ref={editorWrapperRef}
                style={styles.editorWrapper}
                onLayout={onEditorWrapperLayout}
                testID="text-intake-input-wrapper"
              >
                <TextAreaEditor
                  key={editorSeed}
                  initialHtml={seedHtml}
                  onHtmlChange={handleHtmlChange}
                  placeholder="Paste email or text here…"
                  onResolveImage={refuseInlineImage}
                  fillHeight
                />
                {isBlank && skeletonPosition && (
                  <PasteSkeletonHint
                    top={skeletonPosition.top}
                    left={skeletonPosition.left}
                  />
                )}
                {isSubmitting && (
                  <View style={styles.editorBlocker} pointerEvents="auto" />
                )}
              </View>

              <View style={styles.footer}>
                <View style={styles.counterRow}>
                  <Text
                    style={[styles.counter, { color: counterColor }]}
                    testID="text-intake-counter"
                  >
                    {charCount.toLocaleString()} /{' '}
                    {TEXT_INTAKE_MAX_CHARS.toLocaleString()}
                  </Text>
                  {overCap && (
                    <Text style={styles.overCapText}>
                      Too long — trim to fit.
                    </Text>
                  )}
                </View>

                <TouchableOpacity
                  style={styles.emailToggleRow}
                  onPress={toggleEmailThread}
                  disabled={isSubmitting}
                  accessibilityRole="checkbox"
                  accessibilityState={{ checked: emailThread }}
                  accessibilityLabel="Email thread — fill Opportunity fields only"
                  testID="text-intake-email-toggle"
                >
                  <View
                    style={[
                      styles.emailToggleBox,
                      emailThread && styles.emailToggleBoxChecked,
                    ]}
                  >
                    {emailThread && (
                      <Check size={14} color="#FFFFFF" strokeWidth={3} />
                    )}
                  </View>
                  <Text style={styles.emailToggleLabel}>
                    Email thread — fill Opportunity fields only
                  </Text>
                </TouchableOpacity>

                <View style={styles.actions}>
                  <TouchableOpacity
                    style={styles.cancelButton}
                    onPress={handleClose}
                    disabled={isSubmitting}
                    accessibilityLabel="Cancel"
                    accessibilityRole="button"
                    testID="text-intake-cancel"
                  >
                    <Text style={styles.cancelButtonText}>Cancel</Text>
                  </TouchableOpacity>
                  <TouchableOpacity
                    style={[
                      styles.submitButton,
                      !canSubmit && styles.submitButtonDisabled,
                    ]}
                    onPress={handleSubmit}
                    disabled={!canSubmit}
                    accessibilityLabel={submitLabel}
                    accessibilityRole="button"
                    testID="text-intake-submit"
                  >
                    {isSubmitting ? (
                      <ActivityIndicator color="#FFFFFF" size="small" />
                    ) : (
                      <>
                        <WandSparkles size={16} color="#FFFFFF" />
                        <Text style={styles.submitButtonText}>
                          {submitLabel}
                        </Text>
                      </>
                    )}
                  </TouchableOpacity>
                </View>
              </View>
            </View>
            {/* Last child of the overlay, not of the card: the bar is
                position:absolute and needs the full-screen overlay as its
                containing block so it can sit on the keypad's edge. */}
            <RichTextKeyboardDismissBar />
          </View>
        </FormKeyboardScrollProvider>
      </ModalKeyboardProvider>
    </Modal>
  );
};

const styles = StyleSheet.create({
  overlay: {
    flex: 1,
    backgroundColor: 'rgba(0, 0, 0, 0.5)',
    justifyContent: 'center',
    alignItems: 'center',
    padding: 20,
  },
  modalContainer: {
    backgroundColor: '#FFFFFF',
    borderRadius: 20,
    width: '100%',
    // Wide enough for the FULL #1106 toolbar (Issue 6: parity with
    // Additional Information) to hold one row inside the padding.
    maxWidth: 860,
    // #1149 Issue 5: cap to the viewport; flex column lets the editor
    // absorb leftover height so title + footer stay fixed.
    //
    // Native takes a DEFINITE height, not a cap (#1149 Issue 8). `flex: 1` on
    // the editor needs a definite parent: with only maxHeight the column stays
    // content-sized — header + editor + footer never reach the cap on a phone —
    // and a flex child of a content-sized column collapses to zero. Web still
    // caps, because there the editor's intrinsic height exceeds 90% and the
    // clamp makes it definite anyway.
    ...Platform.select({
      web: { maxHeight: '90%' as const },
      default: { height: '92%' as const },
    }),
    flexDirection: 'column',
    overflow: 'hidden',
    padding: 28,
    elevation: 8,
    shadowColor: '#000',
    shadowOffset: { width: 0, height: 4 },
    shadowOpacity: 0.3,
    shadowRadius: 8,
  },
  header: {
    flexShrink: 0,
  },
  footer: {
    flexShrink: 0,
  },
  title: {
    fontSize: 22,
    fontWeight: '700',
    color: colors.textPrimary,
  },
  description: {
    fontSize: 15,
    color: colors.textSecondary,
    lineHeight: 22,
    marginTop: 8,
  },
  toolbarRow: {
    flexDirection: 'row',
    justifyContent: 'flex-end',
    gap: 10,
    marginTop: 16,
    marginBottom: 10,
  },
  iconButton: {
    width: 44,
    height: 44,
    borderRadius: 22,
    borderWidth: 1,
    borderColor: colors.inputBorder,
    backgroundColor: '#FFFFFF',
    alignItems: 'center',
    justifyContent: 'center',
  },
  editorWrapper: {
    // Positioning context for skeleton + submitting blocker overlays.
    // Both platforms now absorb leftover modal height (#1149 Issue 5 on web,
    // Issue 8 on native — the old fixed 300 boxed the paste area into a
    // third of the card). overflow clips the editor's own minHeight floor so a
    // squeezed column cannot paint the editor over the footer.
    position: 'relative',
    flex: 1,
    minHeight: 0,
    overflow: 'hidden',
  },
  editorBlocker: {
    ...StyleSheet.absoluteFillObject,
    backgroundColor: 'rgba(255, 255, 255, 0.5)',
  },
  // top/left are set dynamically: web measures the editor's placeholder
  // element (toolbar height varies as it wraps); native uses a constant.
  skeletonOverlay: {
    position: 'absolute',
    right: 16,
  },
  skeletonCard: {
    width: 250,
    maxWidth: '85%',
    borderWidth: 1,
    borderColor: '#E9EDF2',
    borderRadius: 10,
    backgroundColor: '#FAFBFC',
    padding: 12,
    gap: 8,
  },
  skeletonLine: {
    height: 8,
    borderRadius: 4,
  },
  skeletonLineBlue: {
    backgroundColor: '#B9CDF2',
  },
  skeletonLineGrey: {
    backgroundColor: '#DDE2E8',
  },
  counterRow: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
    paddingTop: 8,
  },
  counter: {
    fontSize: 12,
  },
  overCapText: {
    fontSize: 12,
    color: colors.error,
  },
  emailToggleRow: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 10,
    paddingTop: 12,
  },
  emailToggleBox: {
    width: 20,
    height: 20,
    borderRadius: 5,
    borderWidth: 1.5,
    borderColor: colors.inputBorder,
    alignItems: 'center',
    justifyContent: 'center',
    backgroundColor: '#FFFFFF',
  },
  emailToggleBoxChecked: {
    backgroundColor: colors.primary,
    borderColor: colors.primary,
  },
  emailToggleLabel: {
    flex: 1,
    fontSize: 13,
    color: colors.textPrimary,
    lineHeight: 18,
  },
  actions: {
    flexDirection: 'row',
    justifyContent: 'flex-end',
    gap: 12,
    paddingTop: 20,
  },
  cancelButton: {
    paddingVertical: 12,
    paddingHorizontal: 22,
    borderRadius: 10,
    borderWidth: 1,
    borderColor: colors.inputBorder,
    backgroundColor: '#FFFFFF',
  },
  cancelButtonText: {
    fontSize: 15,
    color: colors.textPrimary,
    fontWeight: '500',
  },
  submitButton: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'center',
    gap: 8,
    paddingVertical: 12,
    paddingHorizontal: 24,
    borderRadius: 10,
    backgroundColor: colors.primary,
    minWidth: 120,
  },
  submitButtonDisabled: {
    backgroundColor: colors.textMuted,
  },
  submitButtonText: {
    fontSize: 15,
    color: '#FFFFFF',
    fontWeight: '600',
  },
});
